import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  signOut,
  updateDisplayName,
  updatePassword,
  updateUsername,
  type Account,
} from '../net/account.js';
import type { Mode, MyStats } from '../net/stats.js';

/**
 * Profil.
 *
 * Ekranın karakteristik göstergesi SIRA DAĞILIMI: dört basamaklı merdivende
 * oyuncunun maçlarının nerede bittiği. "Kaç kazandı, kaç kaybetti" bu oyunda
 * eksik bir özet — dört kişilik masada ikincilik ile sonunculuk arasında
 * dağlar var ve oyuncular kendini bu merdivende tanıyor (oyunun hazır
 * mesajları bile "Buranın kralı benim?" / "Sen ancak veliaht olursun"
 * üzerine kurulu).
 *
 * Renkler puanlamanın kendisinden geliyor: 1. altın, 2. nane, 3. sönük,
 * 4. jeton kırmızısı — masadaki 4·3·2·1 puan merdiveninin aynısı.
 */

const PLACE_LABELS = ['Başgan', 'İkinci', 'Üçüncü', 'Sonuncu'];

function Placements({ row }: { row: MyStats }) {
  const counts = [row.wins, row.seconds, row.thirds, row.lastPlaces];
  const top = Math.max(1, ...counts);

  return (
    <div className="ladder">
      <h4 className="ladder__title">Sıra dağılımı</h4>
      {counts.map((count, index) => (
        <div key={index} className={`ladder__row ladder__row--${index + 1}`}>
          <span className="ladder__place">{index + 1}.</span>
          <span className="ladder__label">{PLACE_LABELS[index]}</span>
          <span className="ladder__track">
            <i style={{ width: `${(count / top) * 100}%` }} />
          </span>
          <b className="ladder__count">{count}</b>
        </div>
      ))}
    </div>
  );
}

function Summary({ row }: { row: MyStats }) {
  const winRate = row.matches > 0 ? Math.round((row.wins / row.matches) * 100) : 0;
  return (
    <div className="figures">
      <div className="figures__main">
        <span className="figure">
          <b>{row.matches}</b>
          <em>maç</em>
        </span>
        <span className="figure">
          <b>{row.totalPoints}</b>
          <em>puan</em>
        </span>
      </div>
      <div className="figures__side">
        <span className="figure figure--sm">
          <b>%{winRate}</b>
          <em>birincilik oranı</em>
        </span>
        <span className="figure figure--sm">
          <b>{row.avgPlace === null ? '–' : row.avgPlace.toFixed(2)}</b>
          <em>ortalama sıra</em>
        </span>
      </div>
    </div>
  );
}

export function Profile({
  account,
  stats,
  onClose,
  onChanged,
}: {
  account: Account;
  stats: MyStats[] | null;
  onClose(): void;
  onChanged(): void;
}) {
  const [mode, setMode] = useState<Mode>('online');
  const [username, setUsername] = useState(account.username);
  const [displayName, setDisplayName] = useState(account.displayName);
  const [password, setPassword] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const run = async (action: () => Promise<string | null>, success: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    const problem = await action();
    setBusy(false);
    if (problem) setError(problem);
    else {
      setNote(success);
      onChanged();
    }
  };

  const row = stats?.find((item) => item.mode === mode) ?? null;

  return (
    <motion.div
      className="scrim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.section
        className="profile"
        initial={{ y: 16, scale: 0.97 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 8, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="profile__head">
          <span className="profile__avatar">{account.displayName.slice(0, 2).toUpperCase()}</span>
          <span className="profile__who">
            <b>{account.displayName}</b>
            <em>@{account.username}</em>
          </span>

          <div className="profile__modes">
            <button
              type="button"
              className={mode === 'online' ? 'chip chip--on' : 'chip'}
              onClick={() => setMode('online')}
            >
              Online
            </button>
            <button
              type="button"
              className={mode === 'bot' ? 'chip chip--on' : 'chip'}
              onClick={() => setMode('bot')}
            >
              Botlar
            </button>
          </div>

          <button type="button" className="profile__close" onClick={onClose} aria-label="Kapat">
            ✕
          </button>
        </header>

        {stats === null ? (
          <p className="profile__empty">Yükleniyor…</p>
        ) : row === null || row.matches === 0 ? (
          <p className="profile__empty">
            {mode === 'online'
              ? 'Henüz gerçek oyuncularla maç bitirmedin.'
              : 'Henüz botlara karşı maç bitirmedin.'}
          </p>
        ) : (
          <div className="profile__body">
            <Summary row={row} />
            <Placements row={row} />
          </div>
        )}

        <details className="settings">
          <summary>Hesap ayarları</summary>

          <div className="settings__grid">
            <label className="field">
              <span>Masada görünen ad</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.slice(0, 24))}
                autoComplete="nickname"
              />
            </label>
            <button
              type="button"
              className="btn btn--tiny"
              disabled={busy || displayName.trim() === account.displayName}
              onClick={() => void run(() => updateDisplayName(displayName), 'Görünen ad değişti.')}
            >
              Kaydet
            </button>

            <label className="field">
              <span>Kullanıcı adı</span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value.slice(0, 20))}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
              />
            </label>
            <button
              type="button"
              className="btn btn--tiny"
              disabled={busy || username.trim().toLowerCase() === account.username}
              onClick={() => void run(() => updateUsername(username), 'Kullanıcı adı değişti.')}
            >
              Değiştir
            </button>

            <label className="field">
              <span>Yeni şifre</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="en az 6 karakter"
                autoComplete="new-password"
              />
            </label>
            <button
              type="button"
              className="btn btn--tiny"
              disabled={busy || password.length < 6}
              onClick={() =>
                void run(async () => {
                  const problem = await updatePassword(password);
                  if (!problem) setPassword('');
                  return problem;
                }, 'Şifre değişti.')
              }
            >
              Güncelle
            </button>
          </div>

          {error && <p className="error">{error}</p>}
          {note && <p className="profile__note">{note}</p>}

          <button type="button" className="btn btn--tiny btn--danger" onClick={() => void signOut()}>
            Çıkış yap
          </button>
        </details>
      </motion.section>
    </motion.div>
  );
}
